import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ClassSelection from '../components/registration/ClassSelection';
import ParentRegistrationForm from '../components/registration/ParentRegistrationForm';
import SchoolAddressSelection from '../components/registration/SchoolAddressSelection';
import SchoolLevelSelection from '../components/registration/SchoolLevelSelection';
import SchoolTypeSelection from '../components/registration/SchoolTypeSelection';
import StepProgressBar from '../components/registration/StepProgressBar';
import SubjectsSelection from '../components/registration/SubjectsSelection';
import GoogleTermsAcceptance from '../components/registration/GoogleTermsAcceptance';
import { RegistrationFormData, School, Subject, ParentData } from '../model/registration/SchoolSelection';
import api from '../utils/api';
import { StepDetails } from './Registration';
import EmailConfirm from '../components/registration/EmailConfirm';
import { useLoading } from '../context/LoadingContext';
import { useError } from '../context/ErrorContext';
import useStepHistory from '../hooks/useStepHistory';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isGoogleFlow = searchParams.get('from') === 'google';

  const defaultRegistrationData: RegistrationFormData = {
    currentStep: 0,
    onboardingData: {},
    selectedSchoolLevel: '',
    selectedSchoolType: '',
    selectedSchoolAddress: '',
    selectedSchoolClass: '',
    selectedSchoolSubjects: [],
    parentData: {
      email: '',
    },
  };

  const loadRegistrationData = (): RegistrationFormData => {
    const storedData = localStorage.getItem('registrationForm');
    if (storedData) {
      return { ...defaultRegistrationData, ...JSON.parse(storedData) };
    }
    return defaultRegistrationData;
  };

  const [schoolLevelOptions, setSchoolLevelOptions] = useState<string[]>([]);
  const [schoolTypeOptions, setSchoolTypeOptions] = useState<string[]>([]);
  const [schoolAddressOptions, setSchoolAddressOptions] = useState<School[]>([]);
  const [classOptions, setClassOptions] = useState<{ name: string; value: string }[]>([]);
  const [subjectsOptions, setSubjectsOptions] = useState<Subject[]>([]);
  const [registrationData, setRegistrationData] = useState<RegistrationFormData>(loadRegistrationData());
  const { setLoading } = useLoading();
  const { setError } = useError();

  const {
    currentStep,
    onboardingData,
    selectedSchoolLevel,
    selectedSchoolType,
    selectedSchoolAddress,
    selectedSchoolClass,
    selectedSchoolSubjects,
    parentData,
  } = registrationData;

  useEffect(() => {
    localStorage.setItem('registrationForm', JSON.stringify(registrationData));
    window.dispatchEvent(new CustomEvent('registrationStepChange', { detail: registrationData.currentStep }));
  }, [registrationData]);

  const updateRegistrationData = <T extends keyof RegistrationFormData>(
    property: T,
    value: RegistrationFormData[T],
  ) => {
    setRegistrationData((prevData) => ({
      ...prevData,
      [property]: value,
    }));
  };

  const updateParentDataField = <T extends keyof ParentData>(field: T, value: ParentData[T]) => {
    setRegistrationData((prevData) => ({
      ...prevData,
      parentData: {
        ...prevData.parentData,
        [field]: value,
      },
    }));
  };

  useEffect(() => {
    setLoading(true);
    api
      .get('/school')
      .then((response) => {
        updateRegistrationData('onboardingData', response.data);
        setSchoolLevelOptions(Object.keys(response.data));
      })
      .catch((error) => {
        console.error(error);
        setError(true, error.response.data.message, error.response.status);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedSchoolLevel && onboardingData[selectedSchoolLevel]) {
      const types = Object.keys(onboardingData[selectedSchoolLevel]);
      setSchoolTypeOptions(types);
      if (selectedSchoolType && onboardingData[selectedSchoolLevel][selectedSchoolType]) {
        const addresses = onboardingData[selectedSchoolLevel][selectedSchoolType];
        setSchoolAddressOptions(addresses);
        if (selectedSchoolAddress && addresses.find((addr) => addr.name === selectedSchoolAddress)) {
          const years =
            selectedSchoolLevel === 'ms'
              ? [
                  { name: 'Primo anno', value: '1' },
                  { name: 'Secondo anno', value: '2' },
                  { name: 'Terzo anno', value: '3' },
                ]
              : [
                  { name: 'Primo anno', value: '1' },
                  { name: 'Secondo anno', value: '2' },
                  { name: 'Terzo anno', value: '3' },
                  { name: 'Quarto anno', value: '4' },
                  { name: 'Quinto anno', value: '5' },
                ];
          setClassOptions(years);
          if (selectedSchoolClass) {
            const subjects = addresses.find((addr) => addr.name === selectedSchoolAddress)?.subjects || [];
            setSubjectsOptions(subjects);
          }
        }
      }
    }
  }, [selectedSchoolAddress, selectedSchoolLevel, selectedSchoolType, selectedSchoolClass, onboardingData]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      updateRegistrationData('currentStep', currentStep + 1);
    } else {
      console.log('Process completed!');
    }
  };

  const handleGoogleRegistration = async () => {
    const school = onboardingData[selectedSchoolLevel]?.[selectedSchoolType]?.find(
      (s: School) => s.name === selectedSchoolAddress,
    );
    const subjectIds = (school?.subjects || [])
      .filter((s: Subject) => selectedSchoolSubjects.includes(s.name))
      .map((s: Subject) => s.id);

    setLoading(true);
    try {
      await api.post('/family/student', {
        school_id: school?.id,
        school_class: selectedSchoolClass,
        subject_ids: subjectIds,
      });
      sessionStorage.removeItem('googleNewUser');
      localStorage.removeItem('registrationForm');
      navigate('/family/home');
    } catch (error: any) {
      api.post('/auth/accept-terms', { terms_and_conditions: 0 }).catch(() => {});
      setError(
        true,
        error.response?.data?.message || 'Errore nella creazione del profilo',
        error.response?.status || 500,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolLevelSelection = (schoolLevel: string) => {
    updateRegistrationData('selectedSchoolLevel', schoolLevel);

    // Se è scuola media o elementare, salta i prossimi 2 step e imposta i valori automaticamente
    if ((schoolLevel === 'ms' || schoolLevel === 'ps') && onboardingData[schoolLevel]) {
      const schoolTypes = Object.keys(onboardingData[schoolLevel]);
      if (schoolTypes.length > 0) {
        const firstSchoolType = schoolTypes[0];
        const schools = onboardingData[schoolLevel][firstSchoolType];

        if (schools && schools.length > 0) {
          const firstSchool = schools[0];

          // Imposta automaticamente tipo e indirizzo scuola
          updateRegistrationData('selectedSchoolType', firstSchoolType);
          updateRegistrationData('selectedSchoolAddress', firstSchool.name);

          // Salta direttamente allo step di selezione classe (step 3)
          updateRegistrationData('currentStep', 3);
          return;
        }
      }
    }

    // Comportamento normale per altri tipi di scuola
    handleNextStep();
  };

  const steps: StepDetails[] = [
    {
      stepComponent: (
        <SchoolLevelSelection
          onNext={handleSchoolLevelSelection}
          options={schoolLevelOptions}
          setSelectedSchoolLevel={(newValue) => updateRegistrationData('selectedSchoolLevel', newValue)}
        />
      ),
      stepId: 'schoolSelection',
    },
    {
      stepComponent: schoolTypeOptions && (
        <SchoolTypeSelection
          onSchoolTypeSelection={() => handleNextStep()}
          options={schoolTypeOptions}
          setSelectedSchoolType={(newValue) => updateRegistrationData('selectedSchoolType', newValue)}
        />
      ),
      stepId: 'schoolType',
    },
    {
      stepComponent: schoolAddressOptions && (
        <SchoolAddressSelection
          onSchoolAddressSelection={() => handleNextStep()}
          options={schoolAddressOptions}
          setSelectedSchoolAddress={(newValue) => updateRegistrationData('selectedSchoolAddress', newValue)}
        />
      ),
      stepId: 'schoolAddress',
    },
    {
      stepComponent: classOptions && (
        <ClassSelection
          onClassSelection={() => handleNextStep()}
          options={classOptions}
          setSelectedSchoolClass={(newValue) => updateRegistrationData('selectedSchoolClass', newValue)}
        />
      ),
      stepId: 'classSelection',
    },
    {
      stepComponent: subjectsOptions && (
        <SubjectsSelection
          onSubjectsSelection={() => handleNextStep()}
          options={subjectsOptions}
          setSelectedSchoolSubjects={(newValue) => updateRegistrationData('selectedSchoolSubjects', newValue)}
        />
      ),
      stepId: 'subjectsSelection',
    },
    {
      stepComponent: isGoogleFlow ? (
        <GoogleTermsAcceptance onAccept={handleGoogleRegistration} />
      ) : (
        <div className="w-full">
          <ParentRegistrationForm
            onSubmit={() => handleNextStep()}
            registrationData={registrationData}
            onboardingData={onboardingData}
            updateParentData={updateParentDataField}
            isGoogleFlow={isGoogleFlow}
          />
        </div>
      ),
      stepId: 'familyData',
    },
    {
      stepComponent: <EmailConfirm />,
      stepId: 'sendEmail',
    },
  ];

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      // Se siamo allo step 3 (classe) e abbiamo scuola media o elementare, torna allo step 0
      if (currentStep === 3 && (selectedSchoolLevel === 'ms' || selectedSchoolLevel === 'ps')) {
        updateRegistrationData('currentStep', 0);
      } else {
        updateRegistrationData('currentStep', currentStep - 1);
      }
    }
  };

  useStepHistory({
    step: currentStep,
    onBack: handlePreviousStep,
    enabled: currentStep > 0 && currentStep < 6,
  });

  // Filtra gli step per la progress bar se scuola media è selezionata
  const getProgressBarSteps = () => {
    if (selectedSchoolLevel === 'ms' || selectedSchoolLevel === 'ps') {
      // Per scuola media o elementare, rimuovi step 1 e 2 (schoolType e schoolAddress)
      return steps.filter((_, index) => index !== 1 && index !== 2);
    }
    return steps;
  };

  // Calcola l'indice corrente per la progress bar
  const getProgressBarCurrentIndex = () => {
    if (selectedSchoolLevel === 'ms' || selectedSchoolLevel === 'ps') {
      if (currentStep === 0) return 0; // schoolSelection
      if (currentStep === 3) return 1; // classSelection
      if (currentStep === 4) return 2; // subjectsSelection
      if (currentStep === 5) return 3; // familyData
      if (currentStep === 6) return 4; // sendEmail
    }
    return currentStep;
  };

  return (
    <div className="bg-fpcwhite w-full py-2 sm:py-4 lg:py-8 px-2 sm:px-4">
      <div className="max-w-5xl mx-auto">
        {/* Banner lezione gratuita */}
        {currentStep < 6 && (
          <div className="text-center mb-4 sm:mb-6 px-2">
            <p className="text-base text-black bg-yellow-300 border-2 border-black rounded-lg px-4 py-3 font-semibold">
             PROVA SENZA IMPEGNO: PER I NUOVI ISCRITTI, LA PRIMA LEZIONE ONLINE È GRATUITA!
            </p>
          </div>
        )}

        {/* Header */}
        {/* <div className="text-center mb-4 sm:mb-6 lg:mb-8 px-2">
          <h1 className="text-2xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-black mb-1 sm:mb-2 mt-4">
            Registrazione <span className="text-fpcred">Famiglia</span>
          </h1>
        </div> */}

        {/* Progress Bar - Mostra solo dopo il primo step */}
        {currentStep > 0 && (
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <StepProgressBar currentStepIndex={getProgressBarCurrentIndex()} steps={getProgressBarSteps()} />
          </div>
        )}

        {/* Navigation */}
        {currentStep > 0 && currentStep !== 6 && (
          <div className="mb-3 sm:mb-4 lg:mb-6 px-2">
            <button
              onClick={handlePreviousStep}
              className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-fpcred hover:text-buttonhover font-medium transition-colors text-sm sm:text-base">
              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Indietro
            </button>
          </div>
        )}

        {/* Step Content */}
        <div className="w-full px-1 sm:px-0">{steps[currentStep].stepComponent}</div>
      </div>
    </div>
  );
};

export default SignUp;
